import * as THREE from 'three';
import type { FpsAudio } from './FpsAudio.js';

const OUTSIDE_GRACE = 2;
const STRIKE_MIN_INTERVAL = 4;
const STRIKE_MAX_INTERVAL = 7;
const STRIKE_OFFSET_MIN = 3;
const STRIKE_OFFSET_MAX = 8;
const BOLT_HEIGHT = 42;
const BOLT_FLASH = 0.2;

export type StormLightningHit = {
  damage: number;
  x: number;
  y: number;
  z: number;
};

type Vec3Like = { x: number; y: number; z: number };

type BoltFx = {
  root: THREE.Group;
  life: number;
};

/**
 * Storm lightning strikes near the player after sustained time outside the safe zone.
 */
export class FpsStormLightning {
  private outsideAcc = 0;
  private strikeCd = 0;
  private readonly bolts: BoltFx[] = [];
  private readonly boltMat = new THREE.MeshBasicMaterial({
    color: 0xe8f4ff,
    transparent: true,
    opacity: 1,
    depthWrite: false,
  });
  private readonly boltGeo = new THREE.CylinderGeometry(0.08, 0.22, BOLT_HEIGHT, 6, 1, true);
  private readonly flashMat = new THREE.MeshBasicMaterial({
    color: 0xc77dff,
    transparent: true,
    opacity: 0.85,
    depthWrite: false,
  });
  private readonly flashGeo = new THREE.RingGeometry(0.4, 2.8, 24);

  constructor(
    private readonly scene: THREE.Scene,
    private readonly audio: FpsAudio,
  ) {}

  update(
    dt: number,
    playerPos: Vec3Like,
    isOutsideStorm: boolean,
    active: boolean,
  ): StormLightningHit | null {
    this.tickBolts(dt);
    if (!active) {
      this.outsideAcc = 0;
      this.strikeCd = 0;
      return null;
    }

    if (!isOutsideStorm) {
      this.outsideAcc = 0;
      this.strikeCd = 0;
      return null;
    }

    this.outsideAcc += dt;
    if (this.outsideAcc < OUTSIDE_GRACE) return null;

    if (this.strikeCd <= 0) {
      this.strikeCd =
        STRIKE_MIN_INTERVAL + Math.random() * (STRIKE_MAX_INTERVAL - STRIKE_MIN_INTERVAL);
      return this.strikeNear(playerPos);
    }

    this.strikeCd -= dt;
    return null;
  }

  private strikeNear(playerPos: Vec3Like): StormLightningHit {
    const angle = Math.random() * Math.PI * 2;
    const dist =
      STRIKE_OFFSET_MIN + Math.random() * (STRIKE_OFFSET_MAX - STRIKE_OFFSET_MIN);
    const x = playerPos.x + Math.cos(angle) * dist;
    const z = playerPos.z + Math.sin(angle) * dist;
    const y = playerPos.y;

    this.spawnBolt(x, y, z);
    this.audio.playStormStrike();

    const damage = 18 + Math.random() * 10;
    return { damage, x, y, z };
  }

  private spawnBolt(x: number, y: number, z: number): void {
    const root = new THREE.Group();
    root.position.set(x, y + BOLT_HEIGHT * 0.5, z);

    const bolt = new THREE.Mesh(this.boltGeo, this.boltMat.clone());
    bolt.rotation.x = Math.PI * 0.04 * (Math.random() - 0.5);
    bolt.rotation.z = Math.PI * 0.04 * (Math.random() - 0.5);
    root.add(bolt);

    const flash = new THREE.Mesh(this.flashGeo, this.flashMat.clone());
    flash.rotation.x = -Math.PI / 2;
    flash.position.y = -BOLT_HEIGHT * 0.5 + 0.05;
    root.add(flash);

    this.scene.add(root);
    this.bolts.push({ root, life: 0 });
  }

  private tickBolts(dt: number): void {
    for (let i = this.bolts.length - 1; i >= 0; i--) {
      const b = this.bolts[i]!;
      b.life += dt;
      const t = b.life / BOLT_FLASH;
      const fade = Math.max(0, 1 - t);
      for (const child of b.root.children) {
        const mat = (child as THREE.Mesh).material as THREE.MeshBasicMaterial;
        mat.opacity = fade * (child instanceof THREE.Mesh && child.geometry === this.flashGeo ? 0.85 : 1);
      }
      if (b.life >= BOLT_FLASH) {
        this.scene.remove(b.root);
        for (const child of b.root.children) {
          const mesh = child as THREE.Mesh;
          (mesh.material as THREE.Material).dispose();
        }
        this.bolts.splice(i, 1);
      }
    }
  }

  dispose(): void {
    for (const b of this.bolts) {
      this.scene.remove(b.root);
      for (const child of b.root.children) {
        const mesh = child as THREE.Mesh;
        (mesh.material as THREE.Material).dispose();
      }
    }
    this.bolts.length = 0;
    this.boltGeo.dispose();
    this.boltMat.dispose();
    this.flashGeo.dispose();
    this.flashMat.dispose();
  }
}
